/**
 * Keskitetyt Supabase-kyselyjen tyypit ja type guardit
 * Korvataan 'as any' -castit kunnollisilla tyypeillä
 */

import type { Json } from "@/integrations/supabase/types";

// ============ JSON-kenttien tyypit ============

/** Koordinaattiarvo config_attribute_values.value_json kentälle */
export interface CoordinatesValue {
  x: number | null;
  y: number | null;
}

/** Reittigeometria installation_loads.route_geometry kentälle */
export interface RouteGeometry {
  type: "LineString";
  coordinates: [number, number][];
}

/** Optimoitu reittijärjestys installation_loads.optimized_order kentälle */
export interface OptimizedWaypoint {
  waypointId: string;
  position: number;
}

// ============ Type guards ============

export function isCoordinatesValue(value: unknown): value is CoordinatesValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "y" in value
  );
}

export function isRouteGeometry(value: unknown): value is RouteGeometry {
  return (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    (value as RouteGeometry).type === "LineString" &&
    "coordinates" in value &&
    Array.isArray((value as RouteGeometry).coordinates)
  );
}

// ============ Model Component with defaults ============

export interface ModelComponentWithDefaults {
  id: string;
  component_type_id: string;
  min_quantity: number;
  max_quantity: number;
  default_material_id: string | null;
  default_width: number | null;
  default_height: number | null;
  default_thickness: number | null;
  position: number;
  label: string | null;
  component_types: {
    id: string;
    name: string;
    label: string;
    dimension_mode: string;
  } | null;
}

/** Model component from query with nested component_type and default_material */
export interface ModelComponentQueryResult {
  id: string;
  component_type_id: string;
  min_quantity: number;
  max_quantity: number;
  default_material_id: string | null;
  default_width: number | null;
  default_height: number | null;
  default_thickness: number | null;
  position: number;
  label: string | null;
  component_type: {
    id: string;
    name: string;
    label: string;
    is_mandatory: boolean;
    allow_multiple: boolean;
    dimension_mode: string;
  } | null;
  default_material: {
    id: string;
    name: string;
  } | null;
}

// ============ Configuration loading types ============

export interface ConfigurationLoadResult {
  id: string;
  name: string | null;
  product_model_id: string;
  product_models: {
    id: string;
    name: string;
    is_custom_template: boolean | null;
  } | null;
}

export interface ComponentTypeMaterialWithName {
  component_type_id: string;
  material_id: string;
  is_default: boolean;
  materials: { id: string; name: string } | null;
}

export interface AttributeDefWithAttribute {
  id: string;
  component_type_id: string;
  attribute_id: string;
  is_required: boolean;
  default_value: string | null;
  position: number;
  attributes: {
    id: string;
    name: string;
    label: string;
    attribute_type: string;
    options: Json | null;
    is_required: boolean;
  } | null;
}

export interface ConfigComponentQueryResult {
  id: string;
  model_component_id: string;
  material_id: string | null;
  quantity: number;
  width: number | null;
  height: number | null;
  thickness: number | null;
  source_model_name: string | null;
  model_components: {
    id: string;
    component_type_id: string;
    min_quantity: number;
    max_quantity: number;
    label: string | null;
    component_types: {
      id: string;
      name: string;
      label: string;
      dimension_mode: string;
    } | null;
  } | null;
}

export interface ConfigAttributeValueQueryResult {
  id: string;
  config_component_id: string;
  attribute_id: string;
  value_text: string | null;
  value_number: number | null;
  value_date: string | null;
  value_json: Json | null;
  attributes: {
    id: string;
    name: string;
    label: string;
    attribute_type: string;
    options: Json | null;
    is_required: boolean;
  } | null;
}

// ============ Material template types ============

export interface MaterialTemplateResult {
  material_id: string;
  default_quantity: number;
  default_width: number;
  default_length: number;
  default_thickness: number;
  notes: string | null;
  materials: { unit_cost: number | null } | null;
}

// ============ Installation query types ============

export interface LoadInstallationQueryResult {
  id: string;
  load_id: string | null;
  load_position: number | null;
  status: string;
  task_type: string;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
  order: {
    order_number: string;
    order_type: string;
    customer: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
    } | null;
    order_lines: Array<{
      id: string;
      description: string;
      quantity: number;
      unit_price: number;
      product: { name: string; sku: string | null } | null;
      configuration: {
        id: string;
        product_model: { name: string } | null;
        config_components: Array<{
          id: string;
          quantity: number;
          width: number | null;
          height: number | null;
          thickness: number | null;
          source_model_name: string | null;
          material: { name: string } | null;
          model_component: {
            position: number;
            component_type: { name: string; label: string } | null;
          } | null;
        }>;
      } | null;
    }>;
  } | null;
  cemetery: {
    name: string;
    city: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
}

// ============ Task types for LoadTasksMap ============

export type TaskStatus = "pending" | "in_progress" | "completed" | "partial" | "skipped" | "cancelled";
export type TaskType = "installation" | "delivery" | "maintenance" | "pickup";
export type OrderType = "new_work" | "pickup_work" | "field_work";

export interface MapTask {
  id: string;
  status: TaskStatus;
  task_type: TaskType;
  load_position: number | null;
  latitude: number | null;
  longitude: number | null;
  cemetery?: {
    name: string;
    city: string;
    address: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  order?: {
    order_number: string;
    order_type: OrderType;
    customer?: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
    } | null;
  } | null;
}

/** Converts LoadInstallation to MapTask for LoadTasksMap component */
export function toMapTask(inst: LoadInstallationQueryResult): MapTask {
  return {
    id: inst.id,
    status: inst.status as TaskStatus,
    task_type: inst.task_type as TaskType,
    load_position: inst.load_position,
    latitude: inst.latitude,
    longitude: inst.longitude,
    cemetery: inst.cemetery,
    order: inst.order
      ? {
          order_number: inst.order.order_number,
          order_type: inst.order.order_type as OrderType,
          customer: inst.order.customer,
        }
      : null,
  };
}
